import React, { useEffect, useRef } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { showToast } from "../components/feedback";
import { resetPageStates } from "../components/modern/pageState";

function AuthLoading(props) {
  const { t } = useTranslation();
  const {
    fetchUser,
    fetchCarTypes,
    fetchSettings,
    fetchBookings,
    fetchCancelReasons,
    fetchUsers,
    signOff,
    fetchWithdraws,
    fetchPaymentMethods,
    fetchLanguages,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSos,
    fetchSMSConfig
  } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const settingsdata = useSelector((state) => state.settingsdata);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch, fetchSettings]);

  // Language packs (common/src/actions/languageactions.js): the list names
  // every language but carries packs only for the default, the user's and any
  // switched to. Every loaded pack goes into i18next (the default one is the
  // English fallback); the saved language is applied ONCE per page load, and
  // fetchUser runs once — later list changes (a pack loaded for a switch, the
  // language editor's full load) must not re-run either.
  const langAppliedRef = useRef(false);
  const userFetchedRef = useRef(false);
  useEffect(() => {
    const list = languagedata.langlist;
    if (!list) return;
    const entries = Object.values(list).filter((v) => v && v.langLocale);
    entries.forEach((v) => {
      if (v.keyValuePairs) i18n.addResourceBundle(v.langLocale, "translations", v.keyValuePairs, true, true);
    });
    const def1 = entries.find((v) => v.default === true);
    if (!langAppliedRef.current && def1) {
      langAppliedRef.current = true;
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('lang') || 'null'); } catch (e) { saved = null; }
      const savedEntry = saved && saved.langLocale ? entries.find((v) => v.langLocale === saved.langLocale && v.keyValuePairs) : null;
      if (savedEntry) {
        i18n.changeLanguage(savedEntry.langLocale);
        moment.locale(saved.dateLocale || savedEntry.dateLocale);
      } else {
        i18n.changeLanguage(def1.langLocale);
        moment.locale(def1.dateLocale);
      }
    }
    if (!userFetchedRef.current) {
      userFetchedRef.current = true;
      dispatch(fetchUser());
    }
  }, [languagedata.langlist, dispatch, fetchUser]);

  // Keep the loaded packs for the next page load: a pack whose hash still
  // matches /languages_meta is reused instead of downloaded again.
  useEffect(() => {
    if (!languagedata.langlist) return;
    try {
      localStorage.setItem('languages_cache', JSON.stringify({ langlist: languagedata.langlist }));
    } catch (e) { /* storage full or blocked — the next load just downloads */ }
  }, [languagedata.langlist]);

  useEffect(() => {
    if (settingsdata.settings) {
      let locale = null;
      let cached = null;
      try {
        const saved = JSON.parse(localStorage.getItem('lang') || 'null');
        locale = saved && saved.langLocale ? saved.langLocale : null;
      } catch (e) { locale = null; }
      try {
        const c = JSON.parse(localStorage.getItem('languages_cache') || 'null');
        cached = c && c.langlist ? c.langlist : null;
      } catch (e) { cached = null; }
      dispatch(fetchLanguages({ locale, cached }));
      dispatch(fetchCarTypes());
      // The browser tab title belongs to the shell (ResponsiveDrawer sets it per
      // page) and to LoginPage; public pages keep index.html's title.
    }
  }, [settingsdata.settings, dispatch, fetchLanguages, fetchCarTypes]);

  const initializedRoleRef = useRef(null);

  useEffect(() => {
    // Wait for /settings before the role fan-out so settings-driven flags
    // (booking window, useTripdateMsIndex, useReportRollups) are read
    // deterministically on the first load after a flip. Settings arrive well
    // before the auth profile in practice; the ref guard below still limits
    // the fan-out to once per role.
    if (auth.profile && settingsdata.settings) {
      if (auth.profile.usertype) {
        let role = auth.profile.usertype;
        // Only dispatch fetch actions once per role (not on every profile field change)
        if (initializedRoleRef.current === role) return;
        initializedRoleRef.current = role;

        // RTDB cost: login pre-fetches are limited to slices with global
        // consumers (drawer badges, quick search, bookings). Earnings reports,
        // notifications, promos, cars (staff roles), NGOs and the all-users
        // wallet history are fetched by the views that render them, on mount.
        if (role === "customer") {
          // Privacy + RTDB cost: no fetchUsers() for customers. A customer's
          // pages (bookings, booking details, add booking, wallet, profile)
          // never read the user list: the two pick-lists built from it are
          // staff-only and QuickSearch is hidden for customers. It used to
          // download every user's record (phones, bank details, ID numbers,
          // ~45 MB) into a customer's browser on each login.
          dispatch(fetchBookings());
          dispatch(fetchWalletHistory());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCancelReasons());
        } else if (role === "driver") {
          dispatch(fetchBookings());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchWalletHistory());
        } else if (role === "admin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchCancelReasons());
          dispatch(fetchWithdraws());
          dispatch(fetchComplain(Date.now() - 30 * 86400000));
          dispatch(fetchPaymentMethods());
          dispatch(fetchSMTP());
          dispatch(fetchSMSConfig());
          dispatch(fetchSos(Date.now() - 30 * 86400000));
        } else if (role === 'callcenteragent' || role === 'callcentersupervisor') {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchCancelReasons());
          if (role === 'callcentersupervisor') {
            // Only the supervisor has the withdraws route + drawer badge.
            dispatch(fetchWithdraws());
          }
          dispatch(fetchComplain(Date.now() - 30 * 86400000));
          dispatch(fetchPaymentMethods());
          dispatch(fetchSos(Date.now() - 30 * 86400000));
        } else if (role === 'finance') {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchCancelReasons());
          dispatch(fetchWithdraws());
          dispatch(fetchComplain(Date.now() - 30 * 86400000));
          dispatch(fetchPaymentMethods());
          dispatch(fetchSos(Date.now() - 30 * 86400000));
        } else if (role === "fleetadmin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchCars());
          dispatch(fetchCancelReasons());
          dispatch(fetchPaymentMethods());
          dispatch(fetchWalletHistory());
        } else if (role === "scout") {
          dispatch(fetchUsers());
        } else {
          // Stays until closed: the sign-out below takes the page back to the login form.
          showToast({ message: t("not_valid_user_type"), kind: 'error', duration: null, id: 'auth-signout' });
          resetPageStates();
          dispatch(signOff());
        }
      } else {
        showToast({ message: t("user_issue_contact_admin"), kind: 'error', duration: null, id: 'auth-signout' });
        resetPageStates();
        dispatch(signOff());
      }
    }
  }, [
    settingsdata.settings,
    auth.profile,
    dispatch,
    fetchBookings,
    fetchCancelReasons,
    fetchUsers,
    fetchWithdraws,
    signOff,
    fetchPaymentMethods,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSMSConfig,
    fetchSos,
    t
  ]);

  return settingsdata.loading ? (
    <CircularLoading />
  ) : settingsdata.settings ? (
    auth.loading || !languagedata.langlist ? (
      <CircularLoading />
    ) : (
      props.children
    )
  ) : (
    <div>
      <span>{t('no_database_settings', { defaultValue: 'No database settings found' })}</span>
  </div>
  );
}

export default AuthLoading;
